import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import SEOMeta from "../../components/base/SEOMeta";

const sections = [
  {
    id: "acceptance",
    title: "1. Acceptance of Terms",
    content: [
      "Welcome to FunsTest! These Terms of Use (\"Terms\") constitute a legally binding agreement between you and FunsTest (\"we\", \"our\", or \"us\") governing your use of the website funstest.com (the \"Site\") and all related quiz content, features, and services offered by FunsTest.",
      "By accessing or using FunsTest — whether by taking a quiz, browsing content, subscribing to our newsletter, or submitting quiz ideas — you confirm that you have read, understood, and agree to be bound by these Terms.",
      "If you do not agree to these Terms, please discontinue use of the Site immediately.",
      "We reserve the right to modify these Terms at any time. Continued use of the Site following the posting of updated Terms constitutes acceptance of those changes. These Terms were last updated on March 25, 2026.",
    ],
  },
  {
    id: "eligibility",
    title: "2. Eligibility & Age Requirements",
    content: [
      "FunsTest is intended for users aged 13 and older. By using our Site, you represent and warrant that you are at least 13 years of age.",
      "**Users Under 13:** If you are under the age of 13, you are not permitted to use FunsTest without verified parental consent. We comply with the Children\'s Online Privacy Protection Act (COPPA). Please see our Privacy Policy for details on how we handle data from younger users.",
      "**Users Under 18:** If you are between the ages of 13 and 17, you represent that your parent or legal guardian has reviewed and agreed to these Terms on your behalf. If you are a parent or guardian permitting a minor to use the Site, you agree to be responsible for their compliance with these Terms.",
      "We reserve the right to suspend or terminate accounts we reasonably believe are being used in violation of these age requirements.",
    ],
  },
  {
    id: "quiz-content",
    title: "3. Quiz Content & Intellectual Property",
    content: [
      "**Our Content:** All quizzes, quiz questions, results, images, text, graphics, logos, and other content published on FunsTest (the \"Content\") are either owned by FunsTest or licensed to us by third-party creators. This Content is protected by copyright, trademark, and other applicable intellectual property laws.",
      "**Limited License to Users:** We grant you a limited, non-exclusive, non-transferable, revocable license to access and use the Site and Content for your personal, non-commercial entertainment purposes only.",
      "**Prohibited Uses:** You may not reproduce, distribute, modify, create derivative works of, publicly display, publicly perform, republish, download, store, or transmit any Content from FunsTest without our prior written consent, except as follows: You may share your individual quiz results on personal social media accounts, provided you include attribution to FunsTest (funstest.com).",
      "**Third-Party IPs:** Some quizzes reference third-party intellectual properties (e.g., Disney, Minecraft, Roblox). FunsTest is not affiliated with, endorsed by, or sponsored by these brands. All referenced trademarks and characters belong to their respective owners. Our use constitutes fan-based editorial commentary.",
      "**Creator Submissions:** If you submit quiz content through our \"Write for Us\" program, you grant FunsTest a non-exclusive, worldwide, royalty-free license to publish, edit, and distribute your submitted content on the Site. You retain ownership of your original work but represent that it does not infringe on any third-party rights.",
    ],
  },
  {
    id: "acceptable-use",
    title: "4. Acceptable Use Policy",
    content: [
      "You agree to use FunsTest only for lawful purposes and in a manner that does not infringe the rights of others. You must NOT:",
      "• Attempt to hack, scrape, crawl, or otherwise extract large volumes of data from the Site using automated tools.",
      "• Impersonate FunsTest, our staff, or any other person or entity.",
      "• Submit false, misleading, or harmful quiz content through any submission form.",
      "• Attempt to introduce malware, viruses, or any harmful code to the Site.",
      "• Use the Site to harass, abuse, or harm other users or third parties.",
      "• Circumvent any security feature, access restriction, or copyright protection on the Site.",
      "• Use the Site for any commercial purpose without our express prior written consent.",
      "• Reproduce or republish quiz content in bulk on other websites or platforms.",
      "We reserve the right to terminate your access to the Site immediately, without notice, if we believe you are in violation of these acceptable use standards.",
    ],
  },
  {
    id: "advertising",
    title: "5. Advertising & Sponsored Content",
    content: [
      "FunsTest is supported by advertising revenue. By using the Site, you acknowledge and agree that advertisements may be displayed throughout the Site, including before, during, and after quiz experiences.",
      "**Google AdSense:** We use Google AdSense to serve advertisements. These ads are delivered by Google and may be personalized based on your browsing history and interests. The appearance of an advertisement on FunsTest does not constitute our endorsement of the advertised product or service.",
      "**Ad Blockers:** You are permitted to use ad-blocking software. However, we rely on advertising revenue to keep our quizzes free. We appreciate your support in keeping FunsTest accessible to everyone by allowing ads on our Site.",
      "**Sponsored Quizzes:** From time to time, FunsTest may publish sponsored quiz content created in partnership with brands. Such content will be clearly labeled as \"Sponsored\" or \"Presented by.\" Sponsorship does not influence our editorial standards or quiz result authenticity.",
    ],
  },
  {
    id: "disclaimers",
    title: "6. Disclaimers & No Warranties",
    content: [
      "FunsTest quizzes are provided for entertainment, fun, and self-reflection purposes only. They are NOT intended to be:",
      "• Psychologically accurate personality assessments",
      "• Medical, mental health, or behavioral diagnoses",
      "• Relationship or life advice",
      "• Astrological or predictive guidance",
      "Quiz results are generated for entertainment value. They are designed to be positive, uplifting, and shareable — not scientifically validated.",
      "**\"As Is\" Basis:** The Site and its Content are provided on an \"as is\" and \"as available\" basis without any warranty of any kind, express or implied. FunsTest makes no warranties regarding the accuracy, reliability, completeness, or suitability of any Content on the Site.",
      "**Availability:** We do not guarantee that the Site will be available at all times, error-free, or free of viruses or other harmful components. We reserve the right to modify, suspend, or discontinue any part of the Site at any time without notice.",
    ],
  },
  {
    id: "liability",
    title: "7. Limitation of Liability",
    content: [
      "To the fullest extent permitted by applicable law, FunsTest, its officers, directors, employees, and affiliates shall not be liable for any:",
      "• Indirect, incidental, special, consequential, or punitive damages",
      "• Loss of profits, data, goodwill, or business opportunities",
      "• Damages resulting from your access to or use of (or inability to access or use) the Site",
      "• Damages resulting from any Content or conduct of any third party on the Site",
      "• Damages resulting from unauthorized access to your personal information",
      "In jurisdictions that do not allow the exclusion or limitation of certain types of liability, our liability shall be limited to the greatest extent permitted by law.",
      "Our total aggregate liability to you for any cause whatsoever, regardless of the form of action, shall not exceed USD $100.",
    ],
  },
  {
    id: "third-party-links",
    title: "8. Third-Party Links & Social Sharing",
    content: [
      "FunsTest may contain links to third-party websites, social media platforms, or other external content. These links are provided for your convenience only.",
      "We have no control over the content, privacy policies, or practices of any third-party sites and accept no responsibility for them. We encourage you to read the privacy policies of any third-party site you visit.",
      "When you share quiz results on social media platforms such as TikTok, Instagram, Pinterest, or Facebook, your use of those platforms is governed by their own terms and privacy policies, not ours.",
    ],
  },
  {
    id: "dmca",
    title: "9. Copyright & DMCA Notice",
    content: [
      "FunsTest respects the intellectual property rights of others. If you believe that content on our Site infringes your copyright, please send a written notification to our designated DMCA agent with the following information:",
      "• A description of the copyrighted work you claim has been infringed",
      "• The URL or location of the allegedly infringing content on our Site",
      "• Your contact information (name, address, email, phone number)",
      "• A statement that you have a good faith belief that use of the material is not authorized",
      "• A statement, under penalty of perjury, that your notification is accurate and that you are the copyright owner or authorized to act on their behalf",
      "Please send DMCA notices to: legal@funstest.com",
      "We will respond promptly to valid DMCA notices and remove or disable access to allegedly infringing content.",
    ],
  },
  {
    id: "termination",
    title: "10. Termination",
    content: [
      "FunsTest reserves the right, at our sole discretion, to restrict, suspend, or terminate your access to all or any part of the Site at any time, with or without notice, for any reason including — but not limited to — violation of these Terms.",
      "Upon termination, all rights and licenses granted to you under these Terms will immediately cease. The following sections of these Terms shall survive any termination: Section 3 (Intellectual Property), Section 6 (Disclaimers), Section 7 (Limitation of Liability), and Section 11 (Governing Law).",
    ],
  },
  {
    id: "governing-law",
    title: "11. Governing Law & Dispute Resolution",
    content: [
      "These Terms are governed by and construed in accordance with the laws of the United States, without regard to its conflict of law provisions.",
      "Any dispute arising from or relating to the subject matter of these Terms shall be subject to the exclusive jurisdiction of the courts located in the United States.",
      "You agree to resolve any disputes with FunsTest on an individual basis and not as part of a class, consolidated, or representative action.",
    ],
  },
  {
    id: "contact",
    title: "12. Contact Us",
    content: [
      "If you have any questions, concerns, or requests regarding these Terms of Use, please contact us:",
      "**Email:** legal@funstest.com",
      "**General Contact:** funstest.com/feedback",
      "We will do our best to respond to all inquiries within 5 business days.",
    ],
  },
];

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const termsJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "Terms of Use | QuizVibe",
  "description": "QuizVibe Terms of Use – rules for using our personality quiz platform. Covers age requirements, intellectual property, acceptable use, and advertising policies.",
  "url": `${SITE_URL}/terms`,
  "inLanguage": "en",
  "dateModified": "2026-03-25",
  "breadcrumb": {
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE_URL },
      { "@type": "ListItem", "position": 2, "name": "Terms of Use", "item": `${SITE_URL}/terms` }
    ]
  },
  "publisher": {
    "@type": "Organization",
    "name": "QuizVibe",
    "url": SITE_URL
  }
};

export default function TermsOfUsePage() {
  return (
    <div className="min-h-screen bg-white">
      <SEOMeta
        title="Terms of Use | QuizVibe – Fun Personality Quizzes"
        description="Read QuizVibe's Terms of Use. Learn about age requirements (13+), quiz content ownership, acceptable use policy, advertising disclosures, and DMCA notices."
        keywords="QuizVibe terms of use, quiz website terms, kids quiz rules, content policy, DMCA"
        canonicalPath="/terms"
        jsonLd={termsJsonLd}
      />
      <Navbar />

      {/* Hero */}
      <section className="pt-28 pb-16 bg-gradient-to-br from-[#FFF8F6] to-[#FFF3ED]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-2 mb-4">
            <Link to="/" className="text-gray-400 hover:text-[#FF6B6B] text-sm transition-colors cursor-pointer">
              Home
            </Link>
            <i className="ri-arrow-right-s-line text-gray-300 text-sm" />
            <span className="text-[#FF6B6B] text-sm font-medium">Terms of Use</span>
          </div>
          <div className="flex items-start gap-6">
            <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-gradient-to-br from-[#FF8E53] to-[#FBBF24] flex-shrink-0 mt-1">
              <i className="ri-file-text-line text-white text-2xl" />
            </div>
            <div>
              <h1
                className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-3"
                style={{ fontFamily: "'Nunito', sans-serif" }}
              >
                Terms of Use
              </h1>
              <p className="text-gray-500 text-base">
                Last updated: <strong>March 25, 2026</strong>
                <span className="mx-3 text-gray-200">|</span>
                Effective date: <strong>January 1, 2021</strong>
              </p>
            </div>
          </div>

          {/* Quick summary bar */}
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { icon: "ri-gamepad-line", label: "Quizzes are for entertainment only", color: "text-[#FF6B6B]" },
              { icon: "ri-user-follow-line", label: "Must be 13+ to use FunsTest", color: "text-[#FF8E53]" },
              { icon: "ri-copyright-line", label: "All quiz content is owned by FunsTest", color: "text-[#FBBF24]" },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-3 bg-white rounded-xl px-4 py-3 border border-orange-50">
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                  <i className={`${item.icon} ${item.color} text-lg`} />
                </div>
                <span className="text-gray-600 text-sm font-medium">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex gap-12">
            {/* TOC sidebar */}
            <aside className="hidden lg:block w-64 flex-shrink-0">
              <div className="sticky top-28">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">On this page</p>
                <nav className="space-y-1">
                  {sections.map((s) => (
                    <a
                      key={s.id}
                      href={`#${s.id}`}
                      className="block text-sm text-gray-500 hover:text-[#FF6B6B] py-1.5 px-3 rounded-lg hover:bg-[#FFF8F6] transition-colors cursor-pointer"
                    >
                      {s.title}
                    </a>
                  ))}
                </nav>
                <div className="mt-8 bg-[#FFF8F6] rounded-2xl p-4 border border-orange-50">
                  <p className="text-xs font-bold text-gray-700 mb-2">Legal questions?</p>
                  <p className="text-xs text-gray-500 mb-3">We\'re here to help clarify anything in plain language.</p>
                  <a
                    href="mailto:legal@funstest.com"
                    className="block text-center text-xs font-bold text-[#FF8E53] bg-white border border-[#FF8E53]/30 rounded-full py-2 hover:bg-[#FF8E53] hover:text-white transition-colors cursor-pointer"
                  >
                    Email Legal Team
                  </a>
                </div>
                <div className="mt-4 p-4 bg-amber-50 rounded-2xl border border-amber-100">
                  <div className="flex items-center gap-2 mb-2">
                    <i className="ri-information-line text-amber-500 text-sm" />
                    <p className="text-xs font-bold text-amber-700">Plain English Summary</p>
                  </div>
                  <p className="text-xs text-amber-600 leading-relaxed">
                    Our quizzes are just for fun! Don\'t copy our content, be kind to others, and enjoy the quizzes.
                  </p>
                </div>
              </div>
            </aside>

            {/* Main content */}
            <div className="flex-1 min-w-0">
              <div className="space-y-12">
                {sections.map((section) => (
                  <div key={section.id} id={section.id} className="scroll-mt-28">
                    <h2
                      className="text-xl font-extrabold text-gray-900 mb-5 pb-3 border-b border-gray-100"
                      style={{ fontFamily: "'Nunito', sans-serif" }}
                    >
                      {section.title}
                    </h2>
                    <div className="space-y-4">
                      {section.content.map((para, i) => {
                        if (para.startsWith("•")) {
                          return (
                            <div key={i} className="flex items-start gap-3">
                              <div className="w-1.5 h-1.5 rounded-full bg-[#FF8E53] mt-2.5 flex-shrink-0" />
                              <p className="text-gray-600 text-sm leading-relaxed">{para.slice(2)}</p>
                            </div>
                          );
                        }
                        const parts = para.split(/\*\*(.*?)\*\*/g);
                        return (
                          <p key={i} className="text-gray-600 text-sm leading-relaxed">
                            {parts.map((part, j) =>
                              j % 2 === 1 ? (
                                <strong key={j} className="text-gray-800 font-semibold">{part}</strong>
                              ) : (
                                part
                              )
                            )}
                          </p>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              {/* Footer note */}
              <div className="mt-16 p-6 bg-gradient-to-br from-[#FFF8F6] to-[#FFF3ED] rounded-2xl border border-orange-50">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className="ri-heart-3-line text-[#FF6B6B] text-lg" />
                  </div>
                  <p className="font-bold text-gray-800 text-sm">The Short Version</p>
                </div>
                <p className="text-gray-500 text-sm leading-relaxed">
                  FunsTest exists to make the internet more fun. Take the quizzes, share your results, laugh with your friends — just don\'t copy our content or do anything harmful. That\'s really all we ask. Now go take a quiz!
                </p>
              </div>

              <div className="mt-8 flex flex-wrap gap-4">
                <Link
                  to="/privacy"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-[#FF6B6B] hover:underline cursor-pointer"
                >
                  <i className="ri-shield-check-line" />
                  View Privacy Policy
                </Link>
                <Link
                  to="/write-for-us"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-gray-500 hover:text-[#FF6B6B] transition-colors cursor-pointer"
                >
                  <i className="ri-edit-line" />
                  Write for FunsTest
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
